import { useEffect, useMemo, useRef, useState, type JSX } from 'react';

import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  useTheme,
  type SxProps,
  type Theme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';

import {
  searchCarriers,
  searchCommodities,
  searchCustomers,
  searchVehicles,
} from '@/api/services/registryService';

export type RegistryKind = 'customer' | 'carrier' | 'commodity' | 'vehicle';

// Display value the autocomplete reads/writes. We normalise customer/carrier
// (`name`) and vehicle (`spz`) into a single `name` field at this boundary
// so the consuming form/editor doesn't have to special-case the vehicle
// shape. The caller knows which kind it asked for and can route the value
// back to the appropriate api field.
export interface RegistryValue {
  id?: string;
  name: string;
}

export interface RegistryAutocompleteProps {
  kind: RegistryKind;
  value: RegistryValue | null;
  onChange: (next: RegistryValue | null) => void;
  label: string;
  required?: boolean;
  /** Disables the field — used by OrderEditor to lock Order-level
   *  fields that a dispatcher cannot mutate (RBAC matrix). */
  disabled?: boolean;
  inputSx?: SxProps<Theme>;
}

const DEBOUNCE_MS = 250;

interface Option extends RegistryValue {
  // Whether this option corresponds to a registry hit (`existing`) or a
  // free-typed string that will trigger RULE-05 auto-create on save (`new`).
  __mode: 'existing' | 'new';
}

const fetcher = async (kind: RegistryKind, query: string): Promise<Option[]> => {
  if (kind === 'customer') {
    const rows = await searchCustomers(query);
    return rows.map((r) => ({ id: r.id, name: r.name, __mode: 'existing' as const }));
  }
  if (kind === 'carrier') {
    const rows = await searchCarriers(query);
    return rows.map((r) => ({ id: r.id, name: r.name, __mode: 'existing' as const }));
  }
  if (kind === 'commodity') {
    const rows = await searchCommodities(query);
    return rows.map((r) => ({ id: r.id, name: r.name, __mode: 'existing' as const }));
  }
  const rows = await searchVehicles(query);
  return rows.map((r) => ({ id: r.id, name: r.spz, __mode: 'existing' as const }));
};

export const RegistryAutocomplete = ({
  kind,
  value,
  onChange,
  label,
  required,
  disabled,
  inputSx,
}: RegistryAutocompleteProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [input, setInput] = useState(value?.name ?? '');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  // Track the latest in-flight query so a stale slow response doesn't clobber
  // a fast fresh one (typing-race correctness).
  const reqIdRef = useRef(0);

  // Keep the input synced with the externally-controlled value (RHF
  // resetting the form, etc.).
  useEffect(() => {
    setInput(value?.name ?? '');
  }, [value?.name]);

  // Debounced fetch — re-run on input or open transitions. Skip if input is
  // identical to the current value's name (no new search needed when the
  // user just refocused without typing).
  useEffect(() => {
    if (!open) return;
    const handle = window.setTimeout(() => {
      const reqId = ++reqIdRef.current;
      setLoading(true);
      fetcher(kind, input)
        .then((rows) => {
          if (reqId !== reqIdRef.current) return;
          setOptions(rows);
        })
        .catch(() => {
          if (reqId !== reqIdRef.current) return;
          setOptions([]);
        })
        .finally(() => {
          if (reqId !== reqIdRef.current) return;
          setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [input, open, kind]);

  // Synthesize a "create new" option when the typed string doesn't match any
  // existing option exactly (RULE-05 auto-create surfaced in the dropdown).
  const visibleOptions = useMemo(() => {
    const trimmed = input.trim();
    if (trimmed.length === 0) return options;
    const exact = options.some(
      (o) => o.name.localeCompare(trimmed, 'cs', { sensitivity: 'accent' }) === 0,
    );
    if (exact) return options;
    const newOpt: Option = { name: trimmed, __mode: 'new' };
    return [...options, newOpt];
  }, [options, input]);

  return (
    <Autocomplete<Option, false, false, true>
      freeSolo
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={visibleOptions}
      filterOptions={(opts) => opts}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name)}
      isOptionEqualToValue={(opt, val) => {
        if (typeof val === 'string') return opt.name === val;
        if (val.id && opt.id) return opt.id === val.id;
        return opt.name === val.name;
      }}
      value={value as Option | null}
      onChange={(_, next) => {
        if (next === null) {
          onChange(null);
          return;
        }
        if (typeof next === 'string') {
          const trimmed = next.trim();
          onChange(trimmed.length > 0 ? { name: trimmed } : null);
          return;
        }
        onChange({ id: next.id, name: next.name });
      }}
      inputValue={input}
      onInputChange={(_, val, reason) => {
        if (reason === 'reset') return;
        setInput(val);
      }}
      onBlur={() => {
        // Commit free-typed value on blur even if the user never selected
        // from the dropdown (matches REG-05 / RULE-05 auto-create UX).
        const trimmed = input.trim();
        if (trimmed.length === 0) {
          if (value !== null) onChange(null);
          return;
        }
        if (value?.name !== trimmed) {
          // Preserve `id` only if the typed text still matches the chosen
          // option exactly; otherwise treat as a new free-typed value.
          if (value?.id && value.name === trimmed) return;
          onChange({ name: trimmed });
        }
      }}
      loading={loading}
      // MUI renders this while `loading && options.length === 0`; without it
      // the Czech-only UI shows MUI's English „Loading…" default.
      loadingText={t('registry.loading')}
      // Vapas dropdown surface (DESIGN.md §6.5 / §6.3): surface bg, 12px radius
      // (card/modal scale), border, resting card shadow.
      slotProps={{
        paper: {
          sx: {
            mt: 0.5,
            borderRadius: 1.5, // 12px (DESIGN.md §4 card/modal radius)
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.elevation.shadow2,
            '& .MuiAutocomplete-option': { fontSize: 13.5, minHeight: 40 },
          },
        },
      }}
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key?: string };
        if (option.__mode === 'new') {
          return (
            <Box
              component="li"
              key={key}
              {...rest}
              sx={{
                gap: 0.75,
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
              data-testid="registry-option-new"
            >
              <AddIcon sx={{ fontSize: 16 }} />
              {t('registry.createNew', { name: option.name })}
            </Box>
          );
        }
        return (
          <Box component="li" key={key} {...rest}>
            {option.name}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          size="small"
          InputProps={{
            ...params.InputProps,
            sx: inputSx,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="primary" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
